/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const MANAGE_AUTHORIZED_CREATORS_DISCRIMINATOR = 9;

export function getManageAuthorizedCreatorsDiscriminatorBytes() {
  return getU8Encoder().encode(MANAGE_AUTHORIZED_CREATORS_DISCRIMINATOR);
}

export type ManageAuthorizedCreatorsInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountProgramAuthority extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountProgramAuthority extends string
        ? WritableAccount<TAccountProgramAuthority>
        : TAccountProgramAuthority,
      TAccountAuthority extends string
        ? ReadonlySignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type ManageAuthorizedCreatorsInstructionData = {
  discriminator: number;
  add: Array<Address>;
  remove: Array<Address>;
};

export type ManageAuthorizedCreatorsInstructionDataArgs = {
  add: Array<Address>;
  remove: Array<Address>;
};

export function getManageAuthorizedCreatorsInstructionDataEncoder(): Encoder<ManageAuthorizedCreatorsInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['add', getArrayEncoder(getAddressEncoder())],
      ['remove', getArrayEncoder(getAddressEncoder())],
    ]),
    (value) => ({
      ...value,
      discriminator: MANAGE_AUTHORIZED_CREATORS_DISCRIMINATOR,
    })
  );
}

export function getManageAuthorizedCreatorsInstructionDataDecoder(): Decoder<ManageAuthorizedCreatorsInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['add', getArrayDecoder(getAddressDecoder())],
    ['remove', getArrayDecoder(getAddressDecoder())],
  ]);
}

export function getManageAuthorizedCreatorsInstructionDataCodec(): Codec<
  ManageAuthorizedCreatorsInstructionDataArgs,
  ManageAuthorizedCreatorsInstructionData
> {
  return combineCodec(
    getManageAuthorizedCreatorsInstructionDataEncoder(),
    getManageAuthorizedCreatorsInstructionDataDecoder()
  );
}

export type ManageAuthorizedCreatorsInput<
  TAccountProgramAuthority extends string = string,
  TAccountAuthority extends string = string,
> = {
  /** The program authority PDA */
  programAuthority: Address<TAccountProgramAuthority>;
  /** The program authority signer */
  authority: TransactionSigner<TAccountAuthority>;
  add: ManageAuthorizedCreatorsInstructionDataArgs['add'];
  remove: ManageAuthorizedCreatorsInstructionDataArgs['remove'];
};

export function getManageAuthorizedCreatorsInstruction<
  TAccountProgramAuthority extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: ManageAuthorizedCreatorsInput<
    TAccountProgramAuthority,
    TAccountAuthority
  >,
  config?: { programAddress?: TProgramAddress }
): ManageAuthorizedCreatorsInstruction<
  TProgramAddress,
  TAccountProgramAuthority,
  TAccountAuthority
> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    programAuthority: {
      value: input.programAuthority ?? null,
      isWritable: true,
    },
    authority: { value: input.authority ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.programAuthority),
      getAccountMeta(accounts.authority),
    ],
    data: getManageAuthorizedCreatorsInstructionDataEncoder().encode(
      args as ManageAuthorizedCreatorsInstructionDataArgs
    ),
    programAddress,
  } as ManageAuthorizedCreatorsInstruction<
    TProgramAddress,
    TAccountProgramAuthority,
    TAccountAuthority
  >);
}

export type ParsedManageAuthorizedCreatorsInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The program authority PDA */
    programAuthority: TAccountMetas[0];
    /** The program authority signer */
    authority: TAccountMetas[1];
  };
  data: ManageAuthorizedCreatorsInstructionData;
};

export function parseManageAuthorizedCreatorsInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedManageAuthorizedCreatorsInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      programAuthority: getNextAccount(),
      authority: getNextAccount(),
    },
    data: getManageAuthorizedCreatorsInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
